import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Dropdown from "react-bootstrap/Dropdown";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import OrganisationLayout from "../layout/OrganisationLayout";
import { dropdownGroupsDashboard, groupData } from "../../Constant/ConstantData";
import {
  organisationDashboardActivityAction,
  organisationDashboardCategoryAction,
  organisationDashboardTrendingTagsAction,
  organisationDashboardUserStatsAction,
  organisationDashboardVideoAction,
  organisationDashboardMostSharedVideosAction,
  organisationDashboardSharedVideoPerSchoolAction,
  organisationDashboardRecentClassesAction,
  organisationDashboardRecentCollectionsAction,
  organisationDashboardRecentActiveStaffAction,
  organisationDashboardStaffStatsAction,
  organisationDashboardCategoryPerSchoolAction,
  organisationDashboardUsageHoursAction,
  organisationListAction,
} from "../../Redux/Action/organisationAction";
import { getActiveGroup } from "../utils/roleAccess";
import { Encryptedid } from "../../Util/BcruptEncyptid";

import { FilterIcon } from "../components/DashboardMessages";
import { OrganizationsView } from "../components/Dashboard/OrganizationsView";
import { LearnerParentView } from "../components/Dashboard/LearnerParentView";
import { StaffView } from "../components/Dashboard/StaffView";

const formatLastActive = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;

  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = String(date.getFullYear()).slice(-2);

  return `Last active ${hours}:${minutes}${ampm} on ${month}/${day}/${year}`;
};

const fallbackThumb = require("../../Assets/Images/clogo.png");
function OrganisationGroupsDashboard() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userData = useSelector((state) => state?.authSlice.userData?.payload);
  const {
    organisationDashboardUserStats,
    organisationDashboardUserStatsStatus,
    organisationDashboardCategoryView,
    organisationDashboardCategoryViewStatus,
    organisationDashboardVideoView,
    organisationDashboardVideoViewStatus,
    organisationDashboardStaffSharedVideos,
    organisationDashboardStaffSharedVideosStatus,
    organisationDashboardSharedVideoPerSchool,
    organisationDashboardSharedVideoPerSchoolStatus,
    organisationDashboardActivity,
    organisationDashboardActivityStatus,
    organisationDashboardTrendingTags,
    organisationDashboardTrendingTagsStatus,
    organisationDashboardRecentClasses,
    organisationDashboardRecentClassesStatus,
    organisationDashboardRecentCollections,
    organisationDashboardRecentCollectionsStatus,
    organisationDashboardRecentActiveStaff,
    organisationDashboardRecentActiveStaffStatus,
    organisationDashboardStaffStats,
    organisationDashboardStaffStatsStatus,
    organisationDashboardCategoryPerSchool,
    organisationDashboardCategoryPerSchoolStatus,
    organisationDashboardUsageHours,
    organisationDashboardUsageHoursStatus,
    organisationDashboardError,
    organisationsList,
  } = useSelector((state) => state?.organisationSlice);
  const organisationGroup = getActiveGroup(userData);
  const [activeRole, setActiveRole] = useState("Learners");
  const [selectedClassForCategory, setSelectedClassForCategory] = useState("");
  const [sortBy, setSortBy] = useState("views");
  const [sortTags, setSortTags] = useState("views");
  const [sortClasses, setSortClasses] = useState("updatedAt");
  const [sortCollections, setSortCollections] = useState("shareDate");
  const [sortOrder, setSortOrder] = useState("asc");
  const [categoryPayload, setCategoryPayload] = useState({});
  const [selectedOrganisation, setSelectedOrganisation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const flattenedOrganizationSharedVideos = useMemo(() => {
    return (
      organisationDashboardSharedVideoPerSchool?.flatMap(
        (school) => school?.topSharedVideos || []
      ) || []
    );
  }, [organisationDashboardSharedVideoPerSchool]);

  const getDefaultDateRange = () => {
    const today = new Date();
    const twelveMonthsAgo = new Date(
      today.getFullYear(),
      today.getMonth() - 12,
      today.getDate(),
    );
    return [twelveMonthsAgo, today];
  };

  const [selectedFilterDateRange, setSelectedFilterDateRange] =
    useState(getDefaultDateRange);

  const learnerAccessible = true;
  const filteredGroups = dropdownGroupsDashboard;
  const organisationSchoolId = useMemo(
    () => selectedOrganisation || "",
    [selectedOrganisation],
  );

  const type = useMemo(() => {
    return activeRole === "Learners"
      ? "Student"
      : activeRole === "Parents"
        ? "Parent"
        : "Staff";
  }, [activeRole]);

  const formatDate = useCallback((date) => {
    if (!date) return null;
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  }, []);

  const dateRange = useMemo(() => {
    const dates =
      selectedFilterDateRange[0] && selectedFilterDateRange[1]
        ? selectedFilterDateRange
        : getDefaultDateRange();
    return {
      startDate: formatDate(dates[0]),
      endDate: formatDate(dates[1]),
    };
  }, [selectedFilterDateRange, formatDate]);

  const staffStatsData = useMemo(() => {
    const rawData = organisationDashboardStaffStats?.dates || [];
    if (rawData.length > 0) {
      return rawData.map((item) => {

        let formattedDate = "";
        if (item.date) {
          const parts = item.date.split("T")[0].split(/[-/]/);
          if (parts.length === 3) {
            if (parts[0].length === 4) {
              formattedDate = `${parts[0]}-${parts[1]}-${parts[2]}`;
            } else if (parts[2].length === 4) {
              formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
          }
        }

        if (!formattedDate) {
          const dateObj = new Date(item.date);
          if (!isNaN(dateObj.getTime())) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
            const dd = String(dateObj.getDate()).padStart(2, "0");
            formattedDate = `${yyyy}-${mm}-${dd}`;
          } else {
            formattedDate = item.date;
          }
        }

        const entry = { date: formattedDate };
        item.schools?.forEach((school) => {
          entry[school.schoolName] = school.count;
        });
        return entry;
      });
    }
    return [];
  }, [organisationDashboardStaffStats]);

  const usageHoursData = useMemo(() => {
    return organisationDashboardUsageHours?.length > 0
      ? organisationDashboardUsageHours.map((item) => ({
        organisation: item.schoolName,
        staff: item.staff || 0,
        parents: item.parents || 0,
        learners: item.learners || 0,
      }))
      : [];
  }, [organisationDashboardUsageHours]);

  const basePayload = useMemo(() => {
    const typeMapping = {
      Learners: "Student",
      Parents: "Parent",
      Staff: "",
    };

    const payload = {
      ...dateRange,
      type: typeMapping[activeRole] || "",
      ...(organisationSchoolId && { schoolId: organisationSchoolId }),
      ...(categoryPayload?.group && { group: Number(categoryPayload.group) }),
      ...(categoryPayload?.year && { year: Number(categoryPayload.year) }),
    };
    return payload;
  }, [activeRole, categoryPayload, dateRange, organisationSchoolId]);

  const handleFilterDateRangeChange = useCallback((dates) => {
    setSelectedFilterDateRange(dates || getDefaultDateRange());
  }, []);

  const getGroupName = useCallback((id) => {
    const groupNames = {
      1: "KS2 Lower",
      2: "KS2 Higher",
      3: "KS3",
      4: "KS4",
      14: "College",
    };
    return groupNames[id] || id;
  }, []);

  const handleSelectForCategory = useCallback(
    (eventKey) => {
      if (typeof eventKey !== "string") {
        setSelectedClassForCategory("");
        setCategoryPayload({});
        return;
      }

      if (eventKey.startsWith("group-")) {
        const groupId = eventKey.replace("group-", "");
        setSelectedClassForCategory(getGroupName(groupId));
        setCategoryPayload({ group: groupId });
      } else if (eventKey.startsWith("year-")) {
        const yearValue = eventKey.replace("year-", "");
        setSelectedClassForCategory(`year-${yearValue}`);
        setCategoryPayload({ year: yearValue });
      }
    },
    [getGroupName],
  );

  useEffect(() => {
    dispatch(organisationListAction({ page: 0, limit: 10 }));
  }, [dispatch]);

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);

      try {
        if (activeRole === "Learners" || activeRole === "Parents") {
          await Promise.all([
            dispatch(organisationDashboardUserStatsAction(basePayload)),
            dispatch(organisationDashboardCategoryAction(basePayload)),
            dispatch(organisationDashboardActivityAction(basePayload)),
            dispatch(organisationDashboardTrendingTagsAction({
              ...basePayload,
              sortBy: sortTags,
            })),
            dispatch(organisationDashboardVideoAction({
              ...basePayload,
              sortBy,
            })),        
          ]);
          return;
        }

        if (activeRole === "Staff") {
          await Promise.all([
            dispatch(organisationDashboardMostSharedVideosAction({
              ...basePayload,
            })),
            dispatch(organisationDashboardRecentClassesAction({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              sortBy: sortClasses,
              sortOrder,
              limit: 10,
              ...(organisationSchoolId && { schoolId: organisationSchoolId }),
            })),
            dispatch(organisationDashboardRecentCollectionsAction({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              sortBy: sortCollections,
              sortOrder,
              limit: 10,
              ...(organisationSchoolId && { schoolId: organisationSchoolId }),
            })),
            dispatch(organisationDashboardRecentActiveStaffAction({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              sortOrder,
              ...(organisationSchoolId && { schoolId: organisationSchoolId }),
            })),
          ]);
          return;
        }

        if (activeRole === "Organizations") {
          await Promise.all([
            dispatch(organisationDashboardSharedVideoPerSchoolAction({
              ...basePayload,
            })),
            dispatch(organisationDashboardStaffStatsAction({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              ...(organisationSchoolId && { schoolId: organisationSchoolId }),
            })),
            dispatch(organisationDashboardCategoryPerSchoolAction({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              ...(organisationSchoolId && { schoolId: organisationSchoolId }),
            })),
            dispatch(organisationDashboardUsageHoursAction({
              startDate: basePayload.startDate,
              endDate: basePayload.endDate,
              ...(organisationSchoolId && { schoolId: organisationSchoolId }),
            })),
          ]);
          return;
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [
    activeRole,
    basePayload,
    dispatch,
    sortBy,
    sortTags,
    sortClasses,
    sortCollections,
    sortOrder,
    organisationSchoolId,
  ]);

  const isDateFilter =
    selectedFilterDateRange &&
    selectedFilterDateRange.length > 0 &&
    selectedFilterDateRange[0] &&
    selectedFilterDateRange[1];

  const registerData = Array.isArray(organisationDashboardUserStats)
    ? organisationDashboardUserStats
    : [];
  const categoryData = Array.isArray(organisationDashboardCategoryView)
    ? organisationDashboardCategoryView
    : [];
  const activityData = useMemo(() => {
    const rawData = Array.isArray(organisationDashboardActivity)
      ? organisationDashboardActivity
      : [];
    return [...rawData].sort((a, b) => {
      const yearA = parseInt(a.year || 0, 10);
      const yearB = parseInt(b.year || 0, 10);
      return yearA - yearB;
    });
  }, [organisationDashboardActivity]);

  const hasActivityData = useMemo(() => {
    if (!activityData || activityData.length === 0) return false;
    return activityData.some(item => (item.activeHours || 0) > 0);
  }, [activityData]);

  const hasUsageHoursData = useMemo(() => {
    if (!usageHoursData || usageHoursData.length === 0) return false;
    return usageHoursData.some(item => (item.staff || 0) > 0 || (item.parents || 0) > 0 || (item.learners || 0) > 0);
  }, [usageHoursData]);
  const trendingTags = Array.isArray(organisationDashboardTrendingTags)
    ? organisationDashboardTrendingTags
    : [];
  const videoData = Array.isArray(organisationDashboardVideoView)
    ? organisationDashboardVideoView
    : [];

  const recentClasses = Array.isArray(organisationDashboardRecentClasses?.classes)
    ? organisationDashboardRecentClasses.classes
    : Array.isArray(organisationDashboardRecentClasses)
      ? organisationDashboardRecentClasses
      : [];

  const totalClassesCount = organisationDashboardRecentClasses?.totalClasses ?? 0;

  const recentCollections = Array.isArray(organisationDashboardRecentCollections?.collections)
    ? organisationDashboardRecentCollections.collections
    : [];

  const totalCollectionsCount = organisationDashboardRecentCollections?.totalCollections ?? 0;

  const recentStaff = Array.isArray(organisationDashboardRecentActiveStaff?.staff)
    ? organisationDashboardRecentActiveStaff.staff
    : [];

  const totalStaffCount = organisationDashboardRecentActiveStaff?.totalStaff ?? 0;

  const showOrganisationDashboardError =
    !isLoading &&
    Boolean(
      organisationDashboardError &&
      [
        organisationDashboardUserStatsStatus,
        organisationDashboardCategoryViewStatus,
        organisationDashboardVideoViewStatus,
        organisationDashboardActivityStatus,
        organisationDashboardTrendingTagsStatus,
        organisationDashboardRecentClassesStatus,
        organisationDashboardRecentCollectionsStatus,
        organisationDashboardRecentActiveStaffStatus,
      ].includes("failed"),
    );

  const commonProps = {
    isLoading, showOrganisationDashboardError, staffStatsData,
    organisationDashboardSharedVideoPerSchoolStatus,
    organisationDashboardSharedVideoPerSchool, fallbackThumb,
    organisationDashboardCategoryPerSchool, usageHoursData, hasUsageHoursData,
    activityData, hasActivityData, registerData, userData, categoryData,
    trendingTags, setSortTags, sortTags, videoData, sortBy, setSortBy,
    recentClasses, totalClassesCount, setSortClasses, sortClasses, sortClassOptions: {
      updatedAt: "Recently updated",
      createdAt: "Recently created",
      withMostCollections: "Most shared"
    },
    sortOrder, setSortOrder, recentCollections, totalCollectionsCount, sortCollectionOptions: {
      shareDate: "Recently shared",
      updatedAt: "Recently updated",
      createdAt: "Recently created",
      mostShared: "Most shared"
    },
    setSortCollections, sortCollections, recentStaff, totalStaffCount,
    organisationDashboardRecentClassesStatus, organisationDashboardRecentCollectionsStatus,
    organisationDashboardRecentActiveStaffStatus, organisationDashboardStaffSharedVideosStatus,
    organisationDashboardStaffSharedVideos,
    isDateFilter, formatLastActive, navigate, Encryptedid, groupData
  };

  return (
    <OrganisationLayout>
      <Container fluid>
        <div className="heading-top mt-5 mb-2">
          <h2>Dashboard for {organisationGroup?.name || "Statistics"}</h2>
        </div>
        <Row>
          <Col lg={12} className="d-flex align-items-center">
            <div className="w-100">
              <div className="new-filter-tab-sec d-flex justify-content-between align-items-center w-100">
                <div className="new-tabs d-flex gap-2 mt-3">
                  {learnerAccessible && (
                    <button
                      className={`dash1 ${activeRole === "Learners" ? "active" : ""}`}
                      onClick={() => setActiveRole("Learners")}
                    >
                      Learners
                    </button>
                  )}
                  <button
                    className={`dash1 ${activeRole === "Parents" ? "active" : ""}`}
                    onClick={() => setActiveRole("Parents")}
                  >
                    Parents
                  </button>
                  <button
                    className={`dash1 ${activeRole === "Staff" ? "active" : ""}`}
                    onClick={() => setActiveRole("Staff")}
                  >
                    Staff
                  </button>
                  <button
                    className={`dash1 ${activeRole === "Organizations" ? "active" : ""}`}
                    onClick={() => setActiveRole("Organizations")}
                  >
                    Organisations
                  </button>
                </div>
                <div className="new-filter-sec d-flex gap-3">
                  <DatePicker
                    selected={selectedFilterDateRange[0]}
                    onChange={handleFilterDateRangeChange}
                    startDate={selectedFilterDateRange[0]}
                    endDate={selectedFilterDateRange[1]}
                    selectsRange
                    dateFormat="dd/MM/yyyy"
                    showIcon
                    placeholderText="Select Date Range"
                    isClearable
                    className="calendar-filter"
                    wrapperClassName="datepicker-wrapper"
                    customInput={<input style={{ width: "300px", padding: "12px 15px 12px 40px", borderRadius: "10px", border: "1px solid #BDE3FF", minHeight: "45px", fontSize: "16px", fontWeight: "500", color: "#2B3674" }} />}
                    maxDate={new Date()}

                  />
                  <Dropdown onSelect={setSelectedOrganisation}>
                    <Dropdown.Toggle
                      variant=""
                      id="dropdown-organisation"
                      className="d-flex align-items-center organisation-filter-pill organisation-filter-dropdown"
                    >
                      <FilterIcon />
                      <p className="m-0 flex-grow-1">
                        {(Array.isArray(organisationsList) ? organisationsList : []).find(org => org._id === selectedOrganisation)?.schoolName || "All Organisations"}
                      </p>
                      {selectedOrganisation && (
                        <FaTimes
                          style={{ cursor: "pointer", marginLeft: 8 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedOrganisation(null);
                          }}
                          title="Clear filter"
                        />
                      )}
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item eventKey={null}>All Organisations</Dropdown.Item>
                      {Array.isArray(organisationsList) && organisationsList.map((org) => (
                        <Dropdown.Item key={org._id} eventKey={org._id}>
                          {org.schoolName}
                        </Dropdown.Item>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                  <Dropdown onSelect={handleSelectForCategory}>
                    <Dropdown.Toggle
                      variant=""
                      id="dropdown-basic"
                      className="d-flex align-items-center"
                    >
                      <FilterIcon />
                      <p className="m-0 flex-grow-1">
                        {selectedClassForCategory || "Year Group"}
                      </p>

                      {selectedClassForCategory && (
                        <FaTimes
                          style={{ cursor: "pointer", marginLeft: 8 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectForCategory(null);
                          }}
                          title="Clear filter"
                        />
                      )}
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      {filteredGroups.map((group) => (
                        <React.Fragment key={group.group}>
                          <Dropdown.Item eventKey={`group-${group.group}`}>
                            {group.label}
                          </Dropdown.Item>
                          {group.years.map((year) => (
                            <Dropdown.Item
                              key={year.value}
                              eventKey={`year-${year.value}`}
                            >
                              {year.name}
                            </Dropdown.Item>
                          ))}
                        </React.Fragment>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
            </div>
          </Col>
        </Row>

        {activeRole === "Organizations" ? (
          <OrganizationsView {...commonProps} />
        ) : activeRole !== "Staff" ? (
          <LearnerParentView {...commonProps} activeRole={activeRole} />
        ) : (
          <StaffView {...commonProps} />
        )}
      </Container>
    </OrganisationLayout>
  );
}

export default memo(OrganisationGroupsDashboard);
